import { UI_PRIMITIVE__button } from '@/components/ui/UI_PRIMITIVE__button';
import { UI_PRIMITIVE__tooltip } from '@/components/ui/UI_PRIMITIVE__tooltip';
import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_hint_shortcut } from '@/hooks/hints_shortcut/use_hint_shortcut';
import { use_slice_generation, use_slice_ui, use_slice_workspace } from '@/hooks/store/use_slices';
import i18n_page_generation from '@/locales/keys/i18n_page_generation.json';
import { PLACEHOLDER_BUTTON__bar_8_5 } from '@/pages/_base/buttons/PLACEHOLDER_BUTTON__bar_8_5';
import { ViewShortcutTrigger } from '@/pages/_base/shortcut_hint/ViewShortcutTrigger';
import { ENUM__i18n_namespace } from '@/services/i18n/ENUM__i18n_namespace';
import { UTILS__graph_data } from '@/utils/UTILS__graph_data';
import { ENUM__workspace_entity_feature } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity_feature';
import { FIELD__list_token } from '@shared/dto/generation/graph_invoke/_fields/_primitives/list/FIELD__list_token';
import { EShortcutType } from '@shared/dto/hint_shortcuts/e_shortcut_type';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface PROPS {
  field_list: FIELD__list_token;
}

export function G_BUTTON__prompt_slider_step({ field_list }: PROPS) {
  /*
   * STORE
   */
  const { t } = useTranslation(ENUM__i18n_namespace.PAGE_GENERATION);
  const tooltip_title = t(i18n_page_generation.sidebar_settings.button.bar_slider_step.tooltip, { field_name: field_list.meta.field_name });

  const dispatch = use_dispatch();

  const { g_general__ui_disabled, global__is_shift_pressed } = use_slice_ui(['global__is_shift_pressed', 'g_general__ui_disabled']);
  const { generation_current } = use_slice_generation(['generation_current']);
  const { current_workspace_user } = use_slice_workspace(['current_workspace_user']);

  /*
   * STATE
   */

  const [is_mouse_over, set_is_mouse_over] = useState(false);

  const { on_user_uses_component, on_user_uses_shortcut } = use_hint_shortcut(EShortcutType.SLIDER_DECREASE_STEP);

  /*
   * EVENTS
   */
  async function onClick() {
    if (is_disabled()) return;

    global__is_shift_pressed ? on_user_uses_shortcut() : on_user_uses_component();

    let value_current = field_list.get_token_weight_slider_step();
    let value_new = get_value_new(value_current, !global__is_shift_pressed);

    if (value_current == value_new) return;

    /*
     * UPDATE
     */

    let field_new = field_list.clone();
    field_new.set_token_weight_slider_step(value_new);

    await UTILS__graph_data.update_field(generation_current.tool.graph, field_new);
  }

  /*
   * METHODS
   */

  function get_value_new(value_current: number, is_next: boolean): number {
    let values: number[] = field_list.get_token_weight_slider_step_values();

    let index_current = values.indexOf(value_current);
    let index_new = is_next ? index_current + 1 : index_current - 1;

    if (index_new < 0) index_new = values.length - 1;
    if (index_new >= values.length) index_new = 0;

    let value_new = values[index_new];

    return value_new;
  }

  function get_value_min() {
    let out_values = field_list.get_token_weight_slider_step_values();

    return out_values[0];
  }

  function get_value() {
    if (is_mouse_over) return field_list.get_token_weight_slider_step();
    if (!global__is_shift_pressed) field_list.get_token_weight_slider_step();

    return field_list.get_token_weight_slider_step();
  }

  function is_disabled(): boolean {
    return g_general__ui_disabled || field_list.meta.ui__is_readonly;
  }

  /*
   * HTML
   */

  if (field_list.get_token_weight_slider_step_values().length < 2) return <PLACEHOLDER_BUTTON__bar_8_5 />; //nothing to change
  if (!current_workspace_user.is_feature_pertmitted(ENUM__workspace_entity_feature.ENTITY_GENERATION_UPDATE)) return <PLACEHOLDER_BUTTON__bar_8_5 />; //cant change

  return (
    <UI_PRIMITIVE__tooltip {...{ tooltip_title }} delay_duration={0}>
      <ViewShortcutTrigger hint_shortcut_keys={[EShortcutType.SLIDER_DECREASE_STEP]}>
        <UI_PRIMITIVE__button
          size="8-5"
          variant="no-border-on-bar"
          className={`text-left text-base text-xs
          ${global__is_shift_pressed ? 'cursor-value-decrease' : 'cursor-value-increase'}`}
          disabled={is_disabled()}
          onMouseEnter={() => set_is_mouse_over(true)}
          onMouseLeave={() => set_is_mouse_over(false)}
          {...{ onClick }}
        >
          ±{get_value()}
        </UI_PRIMITIVE__button>
      </ViewShortcutTrigger>
    </UI_PRIMITIVE__tooltip>
  );
}
